'use client';

import { useState, useRef, useEffect } from 'react';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
}

const countries: Country[] = [
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
];

export default function PhoneInput({ 
  value, 
  onChange, 
  className = 'form-control', 
  style,
  placeholder = 'Phone number',
  id,
  name,
  disabled = false
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    countries.find(c => c.code === 'ZA') || countries[0]
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Parse the existing value to extract country code and phone number
  useEffect(() => {
    if (!value) {
      requestAnimationFrame(() => setPhoneNumber(''));
      return;
    }

    // Find matching country by dial code
    const matchingCountry = countries.find(country => 
      value.startsWith(country.dialCode)
    );
    
    if (matchingCountry) {
      requestAnimationFrame(() => {
        setSelectedCountry(matchingCountry);
        setPhoneNumber(value.substring(matchingCountry.dialCode.length));
      });
    } else {
      // If no matching country code, assume it's just the number
      requestAnimationFrame(() => setPhoneNumber(value));
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    // Strip leading 0 from national number — international format doesn't use it
    const nationalNumber = phoneNumber.replace(/^0+/, '');
    const fullNumber = nationalNumber ? `${country.dialCode}${nationalNumber}` : country.dialCode;
    onChange(fullNumber);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Only allow digits — national number length varies by country
    const cleanedValue = inputValue.replace(/\D/g, '').substring(0, 15);

    // Strip leading 0 from the national number — when a country code is
    // already selected, the international format doesn't include the trunk
    // prefix. e.g. ZA: 0661234567 → +27661234567 (not +270661234567)
    const nationalNumber = cleanedValue.replace(/^0+/, '');

    setPhoneNumber(nationalNumber);
    // Update parent with full international number
    onChange(nationalNumber ? `${selectedCountry.dialCode}${nationalNumber}` : '');
  };

  return (
    <div className="relative">
      <div className="flex">
        {/* Country Code Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            className={`flex items-center px-3 py-2 border theme-border rounded-l-md theme-surface focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-200 ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:theme-surface-hover'}`}
            onClick={() => {
              if (!disabled) {
                setIsDropdownOpen(!isDropdownOpen);
              }
            }}
            disabled={disabled}
            style={{ borderRadius: '3px 0 0 3px', minWidth: '80px' }}
          >
            <span className="text-lg mr-1">{selectedCountry.flag}</span>
            <span className="text-sm font-medium theme-text mr-1">{selectedCountry.dialCode}</span>
            <svg 
              className={`w-4 h-4 theme-text-secondary transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute z-50 mt-1 w-72 theme-surface border theme-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {countries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  className="flex items-center w-full px-3 py-2 text-left hover:theme-surface-hover focus:theme-surface-hover focus:outline-none"
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="text-lg mr-3">{country.flag}</span>
                  <span className="flex-1 text-sm theme-text">{country.name}</span>
                  <span className="text-sm theme-text-secondary ml-2">{country.dialCode}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <input
          id={id}
          name={name}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          disabled={disabled}
          className={`${className} flex-1 rounded-r-md rounded-l-none border-l-0 focus:ring-teal-500 focus:border-teal-500`}
          style={{ 
            ...style, 
            borderRadius: '0 3px 3px 0',
            borderLeft: 'none'
          }}
          placeholder={placeholder}
          maxLength={15}
        />
      </div>
    </div>
  );
}
